import { useQuery } from '@tanstack/react-query';
import { getAnonymousGleapHash, getGleapUserHash } from '@/lib/auth';

export const gleapKeys = {
  userHash: (userId: number) => ['gleap', 'user-hash', userId] as const,
  anonymousHash: () => ['gleap', 'anonymous-hash'] as const,
};

/**
 * Backend-signed Gleap Identity Verification hash for the current user.
 * Keyed on `userId` (not a bare `enabled` flag) so a different user replacing
 * the current one — the shared-machine-login case `GleapIdentitySync` exists
 * to handle — gets its own cache entry and a real refetch, rather than
 * `staleTime: Infinity` silently serving back the previous user's hash.
 */
export function useGleapUserHashQuery(userId: number | undefined) {
  return useQuery({
    queryKey: gleapKeys.userHash(userId ?? -1),
    queryFn: getGleapUserHash,
    enabled: userId !== undefined,
    // HMAC-SHA256(userId, secret) is a pure function of the signed-in user —
    // it never changes for a given session, so there's nothing to refetch.
    staleTime: Infinity,
  });
}

/**
 * Backend-signed hash for an ephemeral anonymous visitor. The backend mints
 * a fresh id + hash on every call (never persisted server-side either), so
 * `staleTime: Infinity` is what makes this "once per page load" rather than
 * once per render — the query only re-fires on a fresh QueryClient (i.e. a
 * real page load, per `main.tsx`).
 */
export function useAnonymousGleapHashQuery(enabled: boolean) {
  return useQuery({
    queryKey: gleapKeys.anonymousHash(),
    queryFn: getAnonymousGleapHash,
    enabled,
    staleTime: Infinity,
  });
}
